/**
 * Adapters bidirectionnels pour le mapping front ↔ API financement V2.
 *
 * Convention :
 *  - Front : camelCase anglais  (ex. monthPayment, dBegin)
 *  - API   : snake_case français (ex. mensualite, date_debut)
 */

// ── Mapping des noms de champs pour `champ_modifie` ──────────────

const fieldNameToApi = {
  dBegin: "date_debut",
  duration: "duree",
  residualValue: "valeur_residuelle",
  monthPaymentReal: "mensualite_retenue",
  // LS-673 — la VR d'un palier se saisit désormais partout via le couple
  // valeur + unité (le champ brut `vr` n'est plus édité directement).
  vrValeurSaisie: "vr_valeur_saisie",
  vrUnite: "vr_unite",
};

export const fieldNameFromApi = Object.fromEntries(
  Object.entries(fieldNameToApi).map(([k, v]) => [v, k]),
);

// ── Statuts ──────────────────────────────────────────────────────

const STATUS_LABELS = {
  1: "Brouillon",
  2: "Demande envoyée",
  3: "Accord reçu",
  4: "Dossier complet",
  5: "Dossier transmis",
  6: "Transmis et signé",
  // LS-629 — statut terminal alternatif, accessible depuis n'importe quel statut.
  7: "Abandonnée",
};

// ── Types de financement ─────────────────────────────────────────

const FINANCE_TYPE_MAP = {
  CREDIT_CLASSIQUE: "Crédit classique",
  CREDIT_BAIL: "Crédit bail",
  LEASE_CAR: "Lease Car",
};

// ── Periode ──────────────────────────────────────────────────────

const periodeToApi = (period) => ({
  numero: period.num,
  date_debut: period.dBegin || null,
  date_fin: period.dEnd || null,
  duree: period.duration === "" ? null : Number(period.duration) || null,
  mensualite:
    period.monthPayment === "" ? null : Number(period.monthPayment) || null,
  mensualite_retenue:
    period.monthPaymentReal === "" ? 0 : Number(period.monthPaymentReal) || 0,
  // LS-673 — VR conservée du palier (en euros), renvoyée telle quelle à l'API :
  // c'est elle que l'API conserve quand aucune saisie manuelle n'est fournie,
  // au lieu de la recalculer depuis la grille constructeur.
  vr: period.vr === "" || period.vr == null ? null : Number(period.vr) || null,
  // LS-673 — saisie manuelle (valeur + unité) désormais possible sur TOUS les
  // paliers, premier inclus : envoyée pour chaque période sans exception.
  vr_unite: period.vrUnite || null,
  vr_valeur_saisie:
    period.vrValeurSaisie === "" || period.vrValeurSaisie == null
      ? null
      : Number(period.vrValeurSaisie),
  // Taux VR (0-1) saisi manuellement quand la grille constructeur n'a pas de
  // correspondance pour la durée/km de ce palier (LS-640) : se persiste et
  // court-circuite la grille côté API tant qu'il est renseigné.
  taux_vr_manuel:
    period.tauxVrManuel === "" || period.tauxVrManuel == null
      ? null
      : Number(period.tauxVrManuel),
  // LS-664 — VR en HT, dérivée côté API (jamais saisie côté front). Repassée
  // telle quelle à l'enregistrement pour ne pas perdre la dernière valeur
  // calculée (ignorée par les routes de calcul, qui la recalculent toujours).
  vr_ht:
    period.vrHT === "" || period.vrHT == null ? null : Number(period.vrHT),
});

// LS-670 — la VR de la dernière période se saisit désormais, comme tous les paliers, via le
// couple vr_valeur_saisie/vr_unite (periodeToApi ci-dessus) : plus d'override dédié depuis
// `residualValue`, devenu redondant et risquant d'écraser une saisie réelle par un écho.
const periodesToApi = (periods) => periods.map(periodeToApi);

const periodeFromApi = (p) => ({
  num: p.numero,
  dBegin: p.date_debut || "",
  dEnd: p.date_fin || "",
  duration: p.duree ?? "",
  monthPayment: p.mensualite ?? "",
  // null distingue « jamais saisi » de « saisi à 0 € » (LS-612) — absent des
  // réponses BlocDevisResource (routes de calcul stateless), présent
  // uniquement sur DemandeDeFinancementResource.
  monthPaymentReal: p.mensualite_retenue ?? null,
  // Écart de mensualité en € (retenue − calculée), null tant qu'aucune
  // mensualité retenue n'est saisie sur ce palier. Calculé côté front :
  // l'API n'expose que ecart_pourcentage.
  ecartEuros:
    p.mensualite_retenue != null && p.mensualite != null
      ? Number(p.mensualite_retenue) - Number(p.mensualite)
      : null,
  // LS-612 — null tant qu'il reste, de ce palier à la fin du devis, au moins
  // une période sans mensualité retenue saisie (échéancier partiel).
  montantAEchoir: p.montant_a_echoir ?? null,
  vr: p.vr ?? 0,
  // LS-664 — équivalent HT de vr, dérivé côté API (vr / coefficient TVA du
  // devis) : affichage uniquement, jamais saisi.
  vrHT: p.vr_ht ?? null,
  vrUnite: p.vr_unite ?? null,
  vrValeurSaisie: p.vr_valeur_saisie ?? null,
  // LS-640 — taux manuel persisté, relu tel quel (null tant qu'aucune saisie).
  tauxVrManuel: p.taux_vr_manuel ?? null,
  // LS-670 — taux VR effectif retenu pour produire `vr` et son origine (devis / grille /
  // saisi / défaut), dérivés côté API : affichage uniquement, jamais saisis ici.
  tauxVrEffectif: p.taux_vr_effectif ?? null,
  origineTauxVr: p.origine_taux_vr ?? null,
  // LS-643 — taux réel implicite des mensualités retenues, calculé côté API
  // (inversion de la formule de loyer). Fraction API (0.05) normalisée en
  // pourcentage (5) ; null tant qu'aucune mensualité retenue n'est saisie ou
  // si le taux n'est pas calculable. Absent des réponses BlocDevisResource
  // (routes de calcul stateless), comme mensualite_retenue.
  tauxReel: p.taux_reel != null ? Number(p.taux_reel) * 100 : null,
  residualValue: 0,
  estDerniere: p.est_derniere || false,
});

// Fusionne les périodes renvoyées par une route de calcul avec l'état local
// précédent, après recalcul / ajout / suppression de période.
//
// `removedIndex` : index de la période supprimée côté front, pour réaligner
// les anciennes périodes sur les nouvelles.
//
// Seule `monthPaymentReal` (LS-612, jamais renvoyée par les routes de calcul) est
// réinjectée depuis l'état local précédent. LS-670 — `vrUnite`/`vrValeurSaisie` ne sont
// plus jamais ré-échoïnées depuis l'ancien état : l'API ne les renvoie désormais que pour
// une saisie réellement effectuée, donc null signifie « pas (ou plus) de saisie sur ce
// palier » et doit être affiché tel quel, jamais masqué par une ancienne valeur locale
// (c'était précisément la contamination qui figeait des VR simplement calculées).
const fusionnerPeriodesRecalculees = (
  anciennesPeriodes = [],
  nouvellesPeriodes = [],
  { removedIndex = null } = {},
) =>
  nouvellesPeriodes.map((p, i) => {
    const oldIndex = removedIndex !== null && i >= removedIndex ? i + 1 : i;
    const old = anciennesPeriodes[oldIndex];
    if (!old) return p;

    return {
      ...p,
      monthPaymentReal: old.monthPaymentReal,
    };
  });

// ── Échéancier mensuel (LS-642) ───────────────────────────────────

const ligneEcheancierMensuelFromApi = (l) => ({
  echeance: l.echeance,
  palierNum: l.palier,
  date: l.date || null,
  mensualiteCalculee: l.mensualite_calculee,
  mensualiteRetenue: l.mensualite_retenue ?? null,
  // LS-731 — TVA mensuelle et mensualité TTC, calculées côté API depuis la zone
  // géographique du catalogue (8,5 % Réunion / 0 % Mayotte). Null tant que la
  // mensualité retenue n'est pas saisie ou si le devis n'est plus résoluble.
  tva: l.tva ?? null,
  mensualiteTTC: l.mensualite_ttc ?? null,
  // Écart en euros (retenue − calculée), pas en %, aligné sur `periodeFromApi`
  // (design 2026-07-23-ecart-euros-design.md) : calcul front, l'API n'expose
  // que ecart_pourcentage.
  ecartEuros:
    l.mensualite_retenue != null && l.mensualite_calculee != null
      ? Number(l.mensualite_retenue) - Number(l.mensualite_calculee)
      : null,
  montantAEchoir: l.montant_a_echoir ?? null,
});

const echeancierMensuelFromApi = (data) => ({
  reference: data.reference || "",
  valeurResiduelleFinale: data.valeur_residuelle_finale ?? null,
  lignes: (data.lignes || []).map(ligneEcheancierMensuelFromApi),
});

// ── Bloc devis (quotation finance) ───────────────────────────────

const blocDevisFromApi = (bloc) => {
  const periods = (bloc.periodes || []).map(periodeFromApi);
  if (periods.length > 0) {
    const last = periods[periods.length - 1];
    last.residualValue = bloc.valeur_residuelle ?? 0;
  }
  return {
    quotation: bloc.devis_uuid,
    reference: bloc.reference || "",
    // LS-643 — taux de financement du devis (fraction API → pourcentage),
    // null si le devis n'est plus accessible côté API.
    tauxFinancementDevis:
      bloc.taux_financement_devis != null
        ? Number(bloc.taux_financement_devis) * 100
        : null,
    periods,
    residualValue: bloc.valeur_residuelle ?? 0,
  };
};

const blocDevisToApi = (q) => {
  const lastPeriod = q.periods[q.periods.length - 1];
  return {
    devis_uuid: q.quotation,
    reference: q.reference || "",
    valeur_residuelle: lastPeriod ? Number(lastPeriod.residualValue) || 0 : 0,
    periodes: periodesToApi(q.periods),
  };
};

// ── Demande (finance request) ────────────────────────────────────

// Checklist des todos d'une demande, toutes étapes confondues (état
// coché/décoché conservé même pour une étape déjà dépassée — irréversible).
const demandeTodoFromApi = (t = {}) => ({
  uuid: t.uuid,
  statut: t.statut,
  libelle: t.libelle || "",
  ordre: t.ordre ?? 0,
  coche: !!t.coche,
  validePar: t.valide_par || null,
  valideLe: t.valide_le || null,
});

const demandeFromApi = (d) => ({
  uuid: d.uuid,
  reference: d.reference || "",
  status: d.statut,
  // LS-716 — n° de contrat attribué par le financeur, saisi manuellement dès
  // Accord reçu (null tant qu'aucune saisie).
  numeroContratFinanceur: d.numero_contrat_financeur || null,
  // LS-717 — compte comptable du dossier (null tant qu'aucune saisie).
  compteComptable: d.compte_comptable || null,
  // L'enveloppe pilote désormais la demande (financeur + type en sont déduits).
  enveloppe: d.enveloppe_uuid || "",
  // Conservés pour l'affichage en-tête (lecture seule) ; non envoyés à l'API.
  financeType: d.type_financement || "",
  financier: d.financeur_uuid || "",
  totalEnvelop: d.enveloppe_total ?? 0,
  quotations: (d.devis || []).map(blocDevisFromApi),
  demandeEnvoyeeLe: d.demande_envoyee_le || null,
  demandeEnvoyeePar: d.demande_envoyee_par || null,
  // LS-672 — sert à éviter de régénérer/ré-uploader le tableau récapitulatif
  // quand la demande n'a pas changé depuis la dernière génération.
  updatedAt: d.updated_at || null,
  // LS-625 — valeurs dénormalisées du dernier envoi/renvoi au financeur, pour
  // préremplir la modale au renvoi. Null tant qu'aucun envoi n'a eu lieu.
  dernierEnvoiFinanceur: d.dernier_envoi_financeur
    ? {
        destinataire: d.dernier_envoi_financeur.destinataire || "",
        cc: d.dernier_envoi_financeur.cc || [],
        fournisseurNom: d.dernier_envoi_financeur.fournisseur_nom || "",
        fournisseurDescription:
          d.dernier_envoi_financeur.fournisseur_description || "",
        commentaire: d.dernier_envoi_financeur.commentaire || "",
      }
    : null,
  // LS-633 — date de passage de chaque étape réellement franchie de l'étapier
  // (une entrée par statut atteint ; absente pour une étape non atteinte ou
  // désactivée sur l'enveloppe).
  etapier: (d.etapier || []).map((e) => ({ statut: e.statut, date: e.date })),
  // Checklist de l'étape courante — toujours modifiable, même verrouillée.
  todos: (d.todos || []).map(demandeTodoFromApi),
  // LS-634 — vrai si chaque devis est couvert par un fichier source ou une
  // saisie manuelle complète de son échéancier.
  echeancierRenseigne: !!d.echeancier_renseigne,
  // LS-668/LS-687 — traçabilité du dernier envoi de l'accord de financement au
  // fournisseur (destinataire, copie, commentaire). Null tant qu'aucun envoi n'a eu lieu.
  accordEnvoyeFournisseur: d.accord_envoye_fournisseur
    ? {
        destinataire: d.accord_envoye_fournisseur.destinataire || "",
        cc: d.accord_envoye_fournisseur.cc || [],
        commentaire: d.accord_envoye_fournisseur.commentaire || "",
        envoyeLe: d.accord_envoye_fournisseur.envoye_le || null,
        envoyePar: d.accord_envoye_fournisseur.envoye_par || null,
      }
    : null,
  // LS-687 — fournisseur choisi à la génération de l'accord (LS-657), pour identifier à
  // qui l'envoyer même après rechargement de page (carnet d'adresses). Null tant qu'aucun
  // accord n'a été généré (ou généré avant cette carte).
  accordFournisseurGenereUuid: d.accord_fournisseur_genere_uuid || null,
});

const demandeToApi = (f) => ({
  enveloppe_uuid: f.enveloppe,
  devis: f.quotations.map(blocDevisToApi),
});

// LS-625 — payload de POST .../envoyer-au-financeur.
const envoyerAuFinanceurToApi = (payload = {}) => ({
  destinataire: payload.destinataire,
  cc: payload.cc || [],
  fournisseur_id: payload.fournisseurId,
  commentaire: payload.commentaire || null,
});

// LS-687 — payload de POST .../envoyer-accord-fournisseur, aligné sur
// envoyerAuFinanceurToApi (cc/commentaire/fournisseur_id ajoutés).
const envoyerAccordFournisseurToApi = (payload = {}) => ({
  destinataire: payload.destinataire,
  cc: payload.cc || [],
  commentaire: payload.commentaire || null,
  fournisseur_id: payload.fournisseurId,
});

// ── Payload helpers ──────────────────────────────────────────────

const recalculerPayload = (
  quotationUuid,
  modifiedIndex,
  modifiedField,
  periods,
) => ({
  devis_uuid: quotationUuid,
  index_periode_modifiee: modifiedIndex,
  champ_modifie: fieldNameToApi[modifiedField] || modifiedField,
  periodes: periodesToApi(periods),
});

const ajouterPeriodePayload = (quotationUuid, periods) => ({
  devis_uuid: quotationUuid,
  periodes: periodesToApi(periods),
});

const supprimerPeriodePayload = (quotationUuid, indexPeriode, periods) => ({
  devis_uuid: quotationUuid,
  index_periode: indexPeriode,
  periodes: periodesToApi(periods),
});

// LS-612 — date_debut optionnelle : n'envoyer la clé que si une date réelle a
// été saisie, pour ne pas déclencher un recalage de dates non voulu.
const saisirMensualiteRetenuePayload = (mensualiteRetenue, dateDebut) => {
  const payload = { mensualite_retenue: Number(mensualiteRetenue) || 0 };
  if (dateDebut) payload.date_debut = dateDebut;
  return payload;
};

// ── Enveloppe response ───────────────────────────────────────────

const enveloppeFromApi = (data) => ({
  totalEnvelop: data.enveloppe_totale,
  details: (data.details || []).map((d) => ({
    quotationUuid: d.devis_uuid,
    investAmtHT: d.montant_investissement_ht,
  })),
});

const enveloppeValidationFromApi = (data) => ({
  isValid: data.est_valide,
  enveloppeOrigine: data.enveloppe_origine ?? 0,
  reserve: data.reserve ?? 0,
  engage: data.engage ?? 0,
  disponible: data.disponible ?? 0,
  difference: data.difference,
  message: data.message,
});

// ── Liste des demandes (élément léger) ───────────────────────────

const demandeListItemFromApi = (item) => ({
  uuid: item.uuid,
  reference: item.reference || "",
  // LS-716 — colonne "N° contrat" de la liste.
  numeroContratFinanceur: item.numero_contrat_financeur || null,
  // LS-717 — affiché à la place de la référence quand il est saisi.
  compteComptable: item.compte_comptable || null,
  statut: {
    valeur: item.statut?.valeur ?? null,
    libelle: item.statut?.libelle ?? "",
  },
  typeFinancement: {
    valeur: item.type_financement?.valeur ?? "",
    libelle: item.type_financement?.libelle ?? "",
  },
  financeur: {
    uuid: item.financeur?.uuid ?? null,
    nom: item.financeur?.nom ?? "—",
  },
  devisReferences: item.devis_references || [],
  // LS-718 — immatriculations normalisées (GA444HY) des véhicules de flotte du
  // dossier ; vide tant que les contrats ne sont pas générés.
  immatriculations: item.immatriculations || [],
  // LS-678 — rattachement demande ↔ ligne de devis par uuid (la référence est
  // un libellé véhicule, non unique au sein d'un dossier).
  devisUuids: item.devis_uuids || [],
  investissementTotal: item.investissement_total ?? 0,
  // LS-634 — badge « Échéancier OK » de la liste.
  echeancierRenseigne: !!item.echeancier_renseigne,
  // LS-647 — badge « X/Y » de progression des todos de la liste.
  todosProgression: item.todos_progression
    ? {
        coches: item.todos_progression.coches ?? 0,
        total: item.todos_progression.total ?? 0,
        todosAFaire: item.todos_progression.todos_a_faire || [],
      }
    : null,
  createdAt: item.created_at || null,
  // LS-628 (addendum) — date de dernier changement de statut (LS-630).
  statutModifieLe: item.statut_modifie_le || null,
});

// ── Consommation d'enveloppe (Réservé / Engagé / Disponible) ─────
// Format plat partagé par GET /financeurs/{uuid}/enveloppe (cumul, avec
// wrapper financeur) et GET /financeurs/{uuid}/enveloppes (cumul + par
// enveloppe, sans wrapper financeur).

const consommationFromFlat = (flat = {}) => ({
  enveloppeOrigine: flat.enveloppe_origine ?? 0,
  reserve: flat.reserve ?? 0,
  engage: flat.engage ?? 0,
  disponible: flat.disponible ?? 0,
});

const consommationEnveloppeFromApi = (body) => {
  // La route /financeurs/{uuid}/enveloppe renvoie la ressource via
  // response()->json(Resource::make()) : payload au niveau racine, sans
  // wrapper « data ». On lit donc directement le body.
  const d = body || {};
  return {
    financeur: {
      uuid: d.financeur?.uuid ?? null,
      nom: d.financeur?.nom ?? "—",
    },
    ...consommationFromFlat(d),
  };
};

// Définition d'un todo par étape d'enveloppe (distinct de l'état coché par
// demande, cf. demandeTodoFromApi) — toujours éditable, y compris verrouillée.
const enveloppeTodoFromApi = (t = {}) => ({
  uuid: t.uuid,
  statut: t.statut,
  libelle: t.libelle || "",
  ordre: t.ordre ?? 0,
});

const enveloppeTodoToApi = (t = {}) => ({
  uuid: t.uuid || null,
  statut: Number(t.statut),
  libelle: t.libelle || "",
  ordre: Number(t.ordre) || 0,
});

const enveloppeItemFromApi = (e = {}) => ({
  uuid: e.uuid,
  financeurUuid: e.financeur_uuid,
  nom: e.nom || "",
  typeFinancement: e.type_financement || "",
  montant: e.montant ?? 0,
  dateDebut: e.date_debut || null,
  dateFin: e.date_fin || null,
  dateMaximale: e.date_maximale || null,
  estActive: !!e.est_active,
  consommation: consommationFromFlat(e.consommation),
  // Parcours de statuts configurable par enveloppe (LS-632).
  etapesActives: e.etapes_actives || [],
  verrouillee: !!e.verrouillee,
  todos: (e.todos || []).map(enveloppeTodoFromApi),
  // Suit encore le gabarit financeur (hérite de ses futures modifications)
  // tant que false ; détachée (surchargée ou verrouillée) si true.
  override: !!e.override,
});

// Gabarit étapes actives / todos par défaut d'un financeur, hérité par ses
// enveloppes non surchargées (override=false).
const financeurConfigFromApi = (c = {}) => ({
  etapesActives: c.etapes_actives || [],
  todos: (c.todos || []).map(enveloppeTodoFromApi),
});

const enveloppesListFromApi = (body = {}) => ({
  consommation: consommationFromFlat(body.consommation),
  enveloppes: (body.enveloppes || []).map(enveloppeItemFromApi),
  financeurConfig: financeurConfigFromApi(body.financeur_config),
});

const enveloppeToApi = (form = {}) => ({
  montant: Number(form.montant) || 0,
  nom: form.nom || null,
  type_financement: form.typeFinancement || null,
  date_maximale: form.dateMaximale || null,
});

// Étapes actives (liste d'ints StatutDemande) : PATCH .../etapes.
const etapesActivesToApi = (etapesActives = []) => ({
  etapes_actives: etapesActives.map(Number),
});

// Liste ordonnée des todos d'une enveloppe : PUT .../todos.
const todosEnveloppeToApi = (todos = []) => ({
  todos: todos.map(enveloppeTodoToApi),
});

// ── Soldes anticipés par véhicule (LS-722) ───────────────────────

const soldeFromApi = (s = {}) => ({
  immatriculation: s.immatriculation,
  dateDemande: s.date_demande || null,
  montant: s.montant != null ? Number(s.montant) : null,
  dateFacture: s.date_facture || null,
  datePaiement: s.date_paiement || null,
  // Statut dérivé côté API : demande_envoyee → facture_recue (montant saisi)
  // → solde (date de paiement).
  statut: s.statut || "demande_envoyee",
});

const suiviSoldesFromApi = (body = {}) => ({
  immatriculationsDisponibles: body.immatriculations_disponibles || [],
  soldes: (body.soldes || []).map(soldeFromApi),
});

// PUT .../soldes/{immat} — remplace l'état complet des trois champs.
const mettreAJourSoldeToApi = (f = {}) => ({
  montant: f.montant === "" || f.montant == null ? null : Number(f.montant),
  date_facture: f.dateFacture || null,
  date_paiement: f.datePaiement || null,
});

// ── Encours Crédit-Bail (LS-623) ─────────────────────────────────
// Une ligne par véhicule financé (demandes Transmis et signé). Les montants
// sont HT et au niveau du devis, sauf montantAEchoirVehicule (à-échoir du
// devis ventilé à parts égales entre ses véhicules — règle actée le 10/09/2026).

const encoursItemFromApi = (item) => ({
  immatriculation: item.immatriculation,
  demandeUuid: item.demande_uuid,
  reference: item.reference || "",
  numeroContratFinanceur: item.numero_contrat_financeur || null,
  // LS-717 — compte du dossier, identique sur tous ses véhicules.
  compteComptable: item.compte_comptable || null,
  financeur: {
    uuid: item.financeur_uuid || null,
    nom: item.financeur_nom || "—",
  },
  devisUuid: item.devis_uuid,
  devisReference: item.devis_reference || "",
  nbVehiculesDevis: item.nb_vehicules_devis ?? 0,
  paliers: (item.paliers || []).map((p) => ({
    numero: p.numero,
    dateDebut: p.date_debut || null,
    dateFin: p.date_fin || null,
    duree: p.duree ?? null,
    mensualiteRetenue: p.mensualite_retenue ?? null,
  })),
  // Null tant que l'échéancier n'est pas intégralement saisi (jamais un
  // montant partiel) ou si le contrat n'a pas démarré (mensualité) / est échu.
  mensualiteCourante: item.mensualite_courante ?? null,
  montantAEchoirDevis: item.montant_a_echoir_devis ?? null,
  montantAEchoirVehicule: item.montant_a_echoir_vehicule ?? null,
  solde: item.solde ? soldeFromApi(item.solde) : null,
  // LS-743 — calculé par l'API : véhicule de flotte à un statut 6x (vendu,
  // facturé ou non) et solde non payé.
  venduNonSolde: Boolean(item.vendu_non_solde),
});

// ── Filtres de liste / dashboard (front camelCase → API snake_case) ─
// Renvoie un objet ; la sérialisation en query string est faite par
// tools.getSearchString côté appelant (qui ignore les valeurs nulles/vides).

const filtresDemandesToApi = (f = {}) => ({
  statut: f.statut,
  financeur_uuid: f.financeurUuid,
  devis_uuid: f.devisUuid,
  devis_reference: f.devisReference,
  client_uuid: f.clientUuid,
  // LS-716 — recherche partielle sur le n° de contrat financeur.
  numero_contrat: f.numeroContrat,
  // LS-717 — recherche partielle et tri par compte comptable décroissant.
  compte_comptable: f.compteComptable,
  tri: f.tri,
  // LS-718 — recherche partielle insensible au format de saisie (ga-444 → GA444HY).
  immatriculation: f.immatriculation,
  type_financement: f.typeFinancement,
  date_debut: f.dateDebut,
  date_fin: f.dateFin,
  echeancier_renseigne: f.echeancierRenseigne,
});

// LS-623 — filtres de l'écran Encours CB. `solde` : "" (tous) / "1" (soldés)
// / "0" (non soldés — un solde seulement demandé reste un encours).
// LS-743 — `venduNonSolde` : "" (tous) / "1" (vendus non soldés seulement).
const filtresEncoursToApi = (f = {}) => ({
  financeur_uuid: f.financeurUuid,
  immatriculation: f.immatriculation,
  solde: f.solde,
  vendu_non_solde: f.venduNonSolde,
});

// Sous-ensemble accepté par la route enveloppe (pas de statut ni de
// financeur_uuid : le financeur est dans l'URL, le statut détermine la couche).
const filtresConsommationToApi = (f = {}) => ({
  devis_uuid: f.devisUuid,
  devis_reference: f.devisReference,
  client_uuid: f.clientUuid,
  type_financement: f.typeFinancement,
  date_debut: f.dateDebut,
  date_fin: f.dateFin,
});

// ── Public API ───────────────────────────────────────────────────

// Part consommée d'une enveloppe, bornée à [0, 100].
const enveloppeRatio = (consomme, origine) =>
  Number(origine) > 0
    ? Math.min((Number(consomme) / Number(origine)) * 100, 100)
    : 0;

// Couleur (vive) pour les barres/jauges selon le taux de consommation.
const enveloppeColor = (ratio) =>
  ratio <= 70 ? "#28a745" : ratio <= 90 ? "#fd7e14" : "#dc3545";

// Variante accessible (contraste >= 4.5:1 sur fond blanc) pour le texte coloré.
const enveloppeColorText = (ratio) =>
  ratio <= 70 ? "#1e7e34" : ratio <= 90 ? "#b35309" : "#c82333";

// La dernière échéance du devis en cours dépasse-t-elle la date_maximale de
// l'enveloppe ? Les deux dates sont des chaînes ISO (YYYY-MM-DD) ; l'absence
// de l'une ou l'autre ne constitue pas un dépassement.
const enveloppeDateMaximaleDepassee = (dateMaximale, derniereEcheance) =>
  !!dateMaximale &&
  !!derniereEcheance &&
  new Date(derniereEcheance) > new Date(dateMaximale);

// LS-693 (suite) — dossier parent d'un devis de la demande. Les groupes issus
// de la recherche et de la demande sont déjà indexés par dossier (uuid +
// reference) : aucun appel API, on fouille ce que l'écran a déjà chargé.
const trouverDossierDuDevis = (sources = [], devisUuid) => {
  for (const dossier of sources) {
    if ((dossier.quotations || []).some((q) => q.uuid === devisUuid)) {
      return { uuid: dossier.uuid, reference: dossier.reference };
    }
  }
  return null;
};

// LS-672 — le tableau récapitulatif est-il toujours à jour par rapport à la
// demande ? Évite de le régénérer/ré-uploader (save/transition) quand rien
// n'a changé depuis la dernière génération.
const tableauRecapitulatifAJour = (demandeUpdatedAt, tableauCreatedAt) =>
  !!demandeUpdatedAt &&
  !!tableauCreatedAt &&
  new Date(tableauCreatedAt) >= new Date(demandeUpdatedAt);

const financeV2Adapters = {
  toApi: {
    periode: periodeToApi,
    blocDevis: blocDevisToApi,
    demande: demandeToApi,
    envoyerAuFinanceur: envoyerAuFinanceurToApi,
    envoyerAccordFournisseur: envoyerAccordFournisseurToApi,
    recalculerPayload,
    ajouterPeriodePayload,
    supprimerPeriodePayload,
    saisirMensualiteRetenuePayload,
    fieldName: (frontField) => fieldNameToApi[frontField] || frontField,
    mettreAJourSolde: mettreAJourSoldeToApi,
    filtresDemandes: filtresDemandesToApi,
    filtresEncours: filtresEncoursToApi,
    filtresConsommation: filtresConsommationToApi,
    enveloppe: enveloppeToApi,
    etapesActives: etapesActivesToApi,
    todosEnveloppe: todosEnveloppeToApi,
  },
  fromApi: {
    periode: periodeFromApi,
    blocDevis: blocDevisFromApi,
    demande: demandeFromApi,
    enveloppe: enveloppeFromApi,
    enveloppeValidation: enveloppeValidationFromApi,
    demandeListItem: demandeListItemFromApi,
    consommationEnveloppe: consommationEnveloppeFromApi,
    consommationFromFlat,
    enveloppeItem: enveloppeItemFromApi,
    enveloppesList: enveloppesListFromApi,
    financeurConfig: financeurConfigFromApi,
    echeancierMensuel: echeancierMensuelFromApi,
    suiviSoldes: suiviSoldesFromApi,
    encoursItem: encoursItemFromApi,
  },
  fusionnerPeriodesRecalculees,
  STATUS_LABELS,
  FINANCE_TYPE_MAP,
  enveloppeRatio,
  enveloppeColor,
  enveloppeColorText,
  enveloppeDateMaximaleDepassee,
  tableauRecapitulatifAJour,
  trouverDossierDuDevis,
};

export default financeV2Adapters;
